import { useState, useEffect, useMemo } from "react";
import { useInspections } from "../../../../hooks/useInspections";
import { useHints } from "../../../../hooks/useHints";
import { ModalRegister, Modal } from "../../../../components/ui/ModalRegister";
import { DrawerPuzzle } from "../../../origami/components/puzzles/DrawerPuzzle";
import RaisedHandsBadge from "../../../../components/ui/RaisedHandsBadge";
import { useInspectProgress } from "../../../../hooks/useInspectProgress";
import InspectProgressChip from "../../../../components/ui/InspectProgressChip";
import { useMissionGuide } from "../../../../hooks/useMissionGuide";
import { getInspectKeys } from "../../../../data/inspectables";
import RoadmapBacklogPuzzle from "../../../origami/components/puzzles/RoadmapBacklogPuzzle";
import { trackError } from "../../../../lib/metrics";
import CableSchemasPanel from "../../../origami/components/puzzles/CableSchemasPanel";
import HintsModalHub from "../../../../components/ui/HintsModalHub";
import OrigamiCommunicationModal from "../../../origami/components/ui/OrigamiCommunicationModal";
import { useOrigamiReboot } from "../../../../hooks/useOrigamiReboot";
import { rebootText } from "../../../../data/origami/origamiTexts";

const API = process.env.REACT_APP_API_URL;

function renderStatus(label, status) {
  const s = status || (label === "BLOC AUXILIAIRE" ? "DÉGRADÉ" : "OFFLINE");
  const cls = s === "ONLINE" ? "text-green-300" : s === "DÉGRADÉ" ? "text-yellow-300" : "text-red-300";
  return <li>{label} — statut : <span className={cls}>{s}</span></li>;
}

/**
 * Stage 1 — Joueur 5 — Composant partagé (5J / 6J)
 *
 * Props spécifiques :
 *   playerCount {number} — 5 ou 6 → getInspectKeys + texte écran + onSuccess roadmap
 */
export default function Stage1Player5Room({
  code,
  playerId,
  hintSignal,
  raisedCount,
  raisedPlayers,
  centerMaxH,
  pseudo,
  playerCount,
}) {
  const INSPECT_KEYS = useMemo(() => {
    return getInspectKeys({ gameId: 1, playerCount, stage: 1, seat: 5 });
  }, [playerCount]);

  // — Modales —
  const [showDrawerModal, setShowDrawerModal] = useState(false);
  const [showScreenModal, setShowScreenModal] = useState(false);
  const [showPostItModal, setShowPostItModal] = useState(false);
  const [showHintModal, setShowHintModal] = useState(false);
  const [showPlannerModal, setShowPlannerModal] = useState(false);
  const [showFakeBinderModal, setShowFakeBinderModal] = useState(false);
  const [showSchemaModal, setShowSchemaModal] = useState(false);
  const [showSchema2Modal, setShowSchema2Modal] = useState(false);
  const [showCharterModal, setShowCharterModal] = useState(false);

  // — États —
  const [drawerUnlocked, setDrawerUnlocked] = useState(false);
  const [binderUnlocked, setBinderUnlocked] = useState(false);
  const [binderCode, setBinderCode] = useState("");
  const [binderError, setBinderError] = useState("");
  const [infraState, setInfraState] = useState(null);
  const [bgReady, setBgReady] = useState(false);

  // — Hooks —
  const { inspectedCount, total } = useInspectProgress({
    sessionCode: code, stage: 1, playerId: Number(playerId), keys: INSPECT_KEYS,
  });
  const { showOrigamiReboot, setShowOrigamiReboot } = useOrigamiReboot({ code, playerId });
  const { markInspectedOnce } = useInspections({ sessionCode: code, stage: 1, playerId: Number(playerId) });
  const { markHintUsedOnce } = useHints({ sessionCode: code, stage: 1, playerId: Number(playerId) });
  const { missionUnlock, missionResolve } = useMissionGuide({ code, playerId, stage: 1 });

  const usedStorageKey = useMemo(() => `hints_used:${code}:s1:p${playerId}`, [code, playerId]);
  const roadmapStorageKey = useMemo(() => `p5:roadmap:${code}:${playerId}`, [code, playerId]);

  // — Assets —
  const backgroundUrl = "/assets/s1-p5-room.webp";
  const drawerImg = "/assets/p5_drawer.webp";
  const screenImg = "/assets/p5_screen.webp";
  const postItImg = "/assets/p5_postit.webp";
  const plannerImg = "/assets/p5_planning.webp";
  const fakeBinderImg = "/assets/p5_fakeBinder.webp";
  const schemaImg = "/assets/p5_schema4Hotspot.webp";
  const schema2Img = "/assets/p5_schema2Hotspot.webp";
  const charterImg = "/assets/p5_charter.webp";

  useEffect(() => { setBgReady(false); }, [backgroundUrl]);
  useEffect(() => { if (!hintSignal) return; setShowHintModal(true); }, [hintSignal]);

  // — Polling infra —
  useEffect(() => {
    let alive = true;
    async function fetchInfra() {
      try {
        const res = await fetch(`${API}/api/sessions/${code}/infra-state`);
        if (!res.ok) return;
        const data = await res.json();
        if (alive) setInfraState(data?.infraState || null);
      } catch { }
    }
    fetchInfra();
    const t = setInterval(fetchInfra, 2500);
    return () => { alive = false; clearInterval(t); };
  }, [code]);

  // — Handlers —
  const handleOpenDrawer = () => {
    setShowDrawerModal(true);
    missionUnlock({ key: "s1.p5.drawer.found", text: `${pseudo} a trouvé un tiroir de bureau vérouillé.` });
  };
  const handleDrawerSolved = () => {
    setDrawerUnlocked(true);
    missionUnlock({ key: "s1.p5.drawer.opened", text: `${pseudo} a déverrouillé le tiroir du bureau… mais la documentation câblage est dans un classeur verrouillé.` });
    missionResolve({ key: "s1.p5.drawer.found" });
  };

  // onSuccess roadmap — micro-différence 5J vs 6J
  const handleRoadmapSuccess = (result) => {
    if (playerCount === 5) {
      const binderUnlockCode = result?.code || "";
      missionUnlock?.({
        key: "s1.p5.roadmap.solved",
        text: `${pseudo} a remis la roadmap dans l'ordre et a obtenue le code du classeur: ${binderUnlockCode}.`,
      });
    } else {
      missionUnlock?.({
        key: "s1.p5.roadmap.obsolete.2016",
        text: `${pseudo} a reconstitué la chronologie : le plan 2016 est obsolète, les schémas actuels sont dans le tiroir.`,
      });
    }
  };

  // — Hints (identiques 5J/6J) —
  const hintCategories = useMemo(() => [
    {
      id: "drawer", title: "Bureau / tiroir", subtitle: "Ouvrir le tiroir",
      hintKeyPrefix: "s1:p5:drawer",
      levels: {
        l1: (<><p className="mb-2">La doc "utile" n'est probablement pas laissée en évidence.</p><p className="text-xs text-gray-300">Inspecte les notes / schémas : ils servent souvent de clé.</p></>),
        l2: (<><p className="mb-2">Le tiroir ne s'ouvrira pas "au hasard".</p><p className="text-xs text-gray-300">Les pictogrammes (▲ ◯ ▭) correspondent à un schéma qu'on retrouve sur la note.</p></>),
        l3: (<><p className="mb-2">Compte le nombre de chaque forme sur le schéma, dans l'ordre demandé.</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (tiroir)</p><p className="mt-2 text-xs text-gray-200">Code du tiroir : <span className="font-bold text-amber-200">623</span></p></>),
      },
    },
    {
      id: "binder", title: "Classeur vérouillé", subtitle: "Trouver le code",
      hintKeyPrefix: "s1:p5:binder",
      levels: {
        l1: (<><p className="mb-2">Le code du classeur n'est pas "dans le tiroir".</p><p className="text-xs text-gray-300">Il se gagne en résolvant une autre énigme de la salle.</p></>),
        l2: (<><p>La roadmap en vrac est la clé : remets les éléments dans l'ordre.</p></>),
        l3: (<><p>Une fois la roadmap validée, ouvre le tiroir et entre le code du classeur.</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (quoi faire)</p><p className="mt-2 text-xs text-gray-200">Résous la <span className="font-semibold">Roadmap infra en vrac</span> pour obtenir le code du classeur, puis saisis-le dans le tiroir.</p></>),
      },
    },
    {
      id: "cables", title: "Exploiter la doc câblage", subtitle: "Quoi partager / retenir",
      hintKeyPrefix: "s1:p5:cables",
      levels: {
        l1: (<><p className="mb-2">La doc n'est pas là pour "cliquer" ici : elle sert à éviter les mauvais branchements.</p><p className="text-xs text-gray-300">Cherche les ports actifs par serveur / rack.</p></>),
        l2: (<><p>Partage la configuration correcte au reste de l'équipe.</p></>),
        l3: (<><p>Peut être que la doc datant de 2016 n'est plus à jour !</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (configuration doc)</p><ul className="mt-2 text-xs text-gray-200 list-disc pl-5 space-y-1"><li>Serveur 1 : <span className="font-mono">A, C</span> (B hors-ligne)</li><li>Serveur 2 : <span className="font-mono">B, C</span> (A &amp; D rechange)</li><li>Rack principal : <span className="font-mono">B, C, H, I</span></li><li>Bloc auxiliaire : <span className="font-mono">A, B</span> (ne pas débrancher)</li></ul></>),
      },
    },
    {
      id: "roadmap", title: "Roadmap désordonnée", subtitle: "Remettre dans l'ordre",
      hintKeyPrefix: "s1:p5:roadmap",
      levels: {
        l1: (<><p className="mb-2">Ce puzzle se résout par logique de séquence : certaines étapes ne peuvent arriver qu'après d'autres.</p></>),
        l2: (<><p className="mb-2">Classe d'abord par date, puis insère les éléments restants en te basant sur les autres informations.</p></>),
        l3: (<><p className="mb-2">Si tu as des difficultés, vérifie l'ordre plusieurs fois, les éléments bien positionnés seront encadrés en vert, les autres, en rouge.</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (quoi faire)</p><p className="mt-2 text-xs text-gray-200">Ordre correct :<br />1) Draft — INFRA PLAN 2016<br />2) Validation architecture (2016)<br />3) Mise en service 'Alpha/Beta/Gamma/Tango'<br />4) MIG-001 — Début de migration vers 'nouvelle infra'<br />5) Remplacement switch cœur<br />6) Décommission partiel (anciens racks)<br />7) Recâblage complet — racks 1/2/principal/4<br />8) Commit — maj schémas officiels + DOCUMENTATION<br />9) Note équipe: 'Plan 2016 obsolète'<br />10) Documentation technique</p></>),
      },
    },
  ], []);

  return (
    <div className="relative min-h-screen w-full bg-black text-white flex items-center justify-center">
      <div className="relative h-screen w-auto mx-auto aspect-square"
        style={{ maxHeight: centerMaxH ? `${centerMaxH}px` : undefined }}>

        {/* HUD */}
        <div className="absolute right-4 top-4 z-40 flex items-center gap-2 w-fit">
          <InspectProgressChip inspectedCount={inspectedCount} total={total} />
          <RaisedHandsBadge count={raisedCount} players={raisedPlayers} />
        </div>
        <div className="absolute left-4 top-4 rounded-full bg-black/70 border border-white/15 px-3 py-1 text-[11px] tracking-[0.18em] uppercase text-slate-200 z-10">
          Salle technique
        </div>

        {/* Fond */}
        <img src={backgroundUrl} alt="Salle technique — Joueur 5"
          className="absolute inset-0 h-full w-full object-contain"
          onLoad={() => setBgReady(true)} onError={() => setBgReady(true)}
        />

        {/* Hotspots */}
        <div className={["absolute inset-0 transition-opacity duration-200", bgReady ? "opacity-100" : "opacity-0 pointer-events-none"].join(" ")}>
          <img src={drawerImg} alt="Tiroir verrouillé"
            onClick={() => { markInspectedOnce('stage1player5:lockedDrawer'); handleOpenDrawer(); }}
            className="p5-drawer absolute cursor-pointer transition-all duration-200 hover:brightness-125 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />
          <img src={plannerImg} alt="Roadmap / planning"
            onClick={() => { markInspectedOnce("stage1player5:planner"); setShowPlannerModal(true); }}
            className="p5-planner absolute cursor-pointer transition-all duration-200 hover:brightness-125 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />
          <img src={screenImg} alt="Écran de supervision"
            onClick={() => { markInspectedOnce('stage1player5:pcScreen'); setShowScreenModal(true); missionUnlock({ key: "s1.p5.screen.found", text: `${pseudo} a trouvé un Journal Système indiquant qu'Origami a généré un fichier chiffré "logs_incidents.sec".` }); }}
            className="p5-screen absolute cursor-pointer transition-all duration-200 hover:brightness-125 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />
          <img src={postItImg} alt="Notes"
            onClick={() => { markInspectedOnce('stage1player5:postIt'); setShowPostItModal(true); }}
            className="p5-postit absolute cursor-pointer transition-all duration-200 hover:brightness-125 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />
          <img src={fakeBinderImg} alt="Ancien classeur d'architecture"
            onClick={() => { markInspectedOnce('stage1player5:fakeBinder'); setShowFakeBinderModal(true); }}
            className="p5-fake-binder absolute cursor-pointer transition-all duration-200 hover:brightness-125 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />
          <img src={schemaImg} alt="Schéma compliqué"
            onClick={() => { markInspectedOnce('stage1player5:schema1'); setShowSchemaModal(true); }}
            className="p5-schema absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />
          <img src={schema2Img} alt="Schéma infra"
            onClick={() => { markInspectedOnce('stage1player5:schema2'); setShowSchema2Modal(true); }}
            className="p5-schema2 absolute cursor-pointer transition-all duration-200 hover:brightness-125 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />
          <img src={charterImg} alt="Affiche Charte numérique responsable"
            onClick={() => { markInspectedOnce('stage1player5:charter'); setShowCharterModal(true); }}
            className="p5-charter absolute cursor-pointer transition-all duration-200 hover:brightness-130 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />
        </div>
      </div>

      {/* === MODALES === */}

      {/* Tiroir */}
      <ModalRegister open={showDrawerModal} title="Tiroir technique verrouillé" onClose={() => setShowDrawerModal(false)} onHint={() => setShowHintModal(true)}>
        {!drawerUnlocked && (
          <>
            <p>Un tiroir lourd, avec une petite étiquette :{" "}<span className="font-mono text-xs bg-black/60 px-2 py-1 rounded">DOCUMENTATION RACKS / NE PAS ÉGARER</span></p>
            <p className="text-sm text-gray-300">Il est verrouillé. Sans le bon code, rien ne bougera.</p>
          </>
        )}
        {drawerUnlocked && <p>Le tiroir est désormais <span className="text-green-300 font-semibold">ouvert</span>.</p>}

        <DrawerPuzzle
          solved={drawerUnlocked} onSolved={handleDrawerSolved}
          sessionCode={code} playerId={playerId}
          unlocked={drawerUnlocked && binderUnlocked}
        />

        {drawerUnlocked && !binderUnlocked && (
          <div className="mt-4 rounded-xl border border-amber-400/25 bg-amber-500/10 p-3">
            <p className="text-sm text-amber-100 font-semibold">📁 Classeur "CÂBLAGES RACKS" verrouillé</p>
            <p className="mt-1 text-xs text-slate-200">Le tiroir contient un classeur fermé par un cadenas à 10 chiffres.</p>
            <div className="mt-3 flex items-center gap-2">
              <input
                value={binderCode}
                onChange={(e) => { setBinderCode(e.target.value); setBinderError(""); }}
                placeholder="Code…"
                className="w-40 rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm font-mono text-slate-100 outline-none focus:border-amber-400/50"
              />
              <button type="button"
                onClick={() => {
                  let expected = "";
                  try {
                    const raw = localStorage.getItem(roadmapStorageKey);
                    const parsed = raw ? JSON.parse(raw) : null;
                    expected = parsed?.code || "";
                  } catch { }
                  if (!expected) { 
                    setBinderError("Code incorrect."); 
                    trackError?.({ sessionCode: code, stage: 1, playerId, reason: "stage1_p5_binder_wrong_code" });
                    return; 
                  }
                  if (binderCode.trim() !== expected) {
                    setBinderError("Code incorrect.");
                    trackError?.({ sessionCode: code, stage: 1, playerId, reason: "stage1_p5_binder_wrong_code" });
                    return;
                  }
                  setBinderUnlocked(true);
                  setBinderError("");
                  missionUnlock?.({ key: "s1.p5.binder.opened", text: `${pseudo} a déverrouillé le classeur de câblage trouvé dans le tiroir.` });
                }}
                className="rounded-xl bg-indigo-600 hover:bg-indigo-500 px-4 py-2 text-xs font-semibold"
              >
                Déverrouiller
              </button>
            </div>
            {binderError && <p className="mt-2 text-xs text-rose-200">{binderError}</p>}
          </div>
        )}

        {drawerUnlocked && binderUnlocked && (
          <div className="mt-4 rounded-xl border border-emerald-400/25 bg-emerald-500/10 p-3">
            <p className="text-sm text-emerald-100 font-semibold">✅ Classeur ouvert : schéma de câblage (actuel)</p>
            <p className="mt-1 text-xs text-slate-200">La documentation est enfin accessible.</p>
            <div className="mt-3 rounded-xl border border-white/10 bg-black/40 p-3">
              <CableSchemasPanel />
              <p className="mt-2 text-[11px] text-slate-400 italic text-center">Schéma de câblage officiel — racks / serveurs</p>
            </div>
          </div>
        )}
      </ModalRegister>

      {/* Roadmap */}
      <ModalRegister open={showPlannerModal} title="Roadmap infra en vrac" onClose={() => setShowPlannerModal(false)} onHint={() => setShowHintModal(true)} containerClassName="!max-w-[800px] w-[96vw]">
        <RoadmapBacklogPuzzle
          storageKey={roadmapStorageKey}
          onSuccess={handleRoadmapSuccess}
          onAttempt={({ ok }) => { if (!ok) trackError?.({ sessionCode: code, stage: 1, playerId, reason: "stage1_p5_roadmap_wrong_order" }); }}
        />
      </ModalRegister>

      {/* Vieux classeur */}
      <Modal open={showFakeBinderModal} title="Livre poussiéreux" onClose={() => setShowFakeBinderModal(false)}>
        <p className="text-sm text-gray-200">Un vieux livre jauni et plein de poussière, vous parvenez à lire :{" "}<span className="font-mono bg-black/60 px-2 py-1 rounded">INFRA — PLAN 2016</span></p>
        <div className="mt-3 space-y-2 text-xs font-mono">
          {[["SERVEUR ALPHA", "D, E, F"], ["SERVEUR BETA", "A, D, G"], ["SERVEUR GAMMA", "C, F, H"], ["SERVEUR TANGO", "B, R, T"]].map(([name, ports]) => (
            <div key={name} className="rounded-lg border border-white/10 bg-black/40 p-2">
              <p className="font-semibold text-pink-200">{name}</p>
              <p>Ports actifs : {ports}</p>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-gray-400">Une annotation au crayon : <span className="italic">"À archiver / décommissionner proprement (recyclage matériel) quand la nouvelle infra sera totalement en place."</span></p>
      </Modal>

      {/* Schéma 1 */}
      <ModalRegister open={showSchemaModal} title="Un schéma totalement illisible" onClose={() => setShowSchemaModal(false)}>
        <div className="rounded-xl border border-white/10 bg-black/40 p-3">
          <img src="/assets/p5_schema3.webp" alt="Schéma technique griffonné au tableau" className="w-full h-auto max-h-[500px] object-contain mx-auto" />
          <p className="mt-2 text-xs text-gray-400 italic text-center">Un schéma rempli de cercles, flèches et annotations minuscules.</p>
        </div>
      </ModalRegister>

      {/* Schéma 2 */}
      <ModalRegister open={showSchema2Modal} title="On dirait un schéma technique" onClose={() => setShowSchema2Modal(false)}>
        <p className="text-sm mb-3">Hmmm...</p>
        <div className="rounded-xl border border-white/10 bg-black/40 p-3">
          <img src="/assets/p5_schema2.webp" alt="Schéma d'architecture réseau" className="w-full h-auto max-h-[500px] object-contain mx-auto" />
          <p className="mt-2 text-xs text-gray-400 italic text-center">Des rectangles reliés par des lignes, des annotations du type &quot;CORE-NODE&quot;, &quot;SUBSYS A&quot;, &quot;DATAFLOW&quot;… Ça ressemble à quelque chose que seule l&apos;équipe réseau comprend. Et pourquoi ça parle de Bus...?</p>
        </div>
      </ModalRegister>

      {/* Écran supervision */}
      <ModalRegister open={showScreenModal} title="Écran de supervision" onClose={() => setShowScreenModal(false)}>
        <p>L'écran affiche un vieux tableau de supervision réseau. La plupart des alertes sont rouges ou orange.</p>
        <ul className="mt-2 list-disc pl-5 text-xs space-y-1">
          {renderStatus("SERVEUR 1", infraState?.computed?.perRack?.server1)}
          {renderStatus("SERVEUR 2", infraState?.computed?.perRack?.server2)}
          {renderStatus("SERVEUR 3", "ONLINE")}
          {renderStatus("SERVEUR PRINCIPAL", infraState?.computed?.perRack?.serverPrincipal)}
          {renderStatus("BLOC AUXILIAIRE", infraState?.computed?.perRack?.server4)}
        </ul>
        <p className="mt-2 text-indigo-300 text-xs">Aucun bouton ne semble permettre de changer quoique ce soit...</p>
        <div className="mt-4 rounded-lg border border-indigo-400/40 bg-black/60 p-3 text-[11px] font-mono text-gray-100">
          <p className="mb-1 text-indigo-200 font-semibold">JOURNAL SYSTÈME — ORIGAMI</p>
          <p>[22:11:03] ORIGAMI: détection d'anomalies critiques.</p>
          <p>[22:11:07] ORIGAMI: génération du fichier chiffré <span className="text-emerald-300">logs_incidents.sec</span></p>
          <p>[22:11:08] CIBLE: poste utilisateur ID <span className="font-bold">359</span> (M.D.)</p>
          <p>[22:11:09] NOTE: accès restreint — protection par mot de passe.</p>
          <p className="mt-2 text-indigo-300">
            Commentaire opérateur : « Évidemment, Origami a encore choisi <span className="italic">son langage préféré</span> pour protéger ça…{" "}
            {playerCount === 6 && "il adorait nous parler en morse."}»
          </p>
        </div>
        <p className="mt-3 text-[11px] text-gray-300">En bas de l'écran, un rappel interne : <span className="italic">"Moins d'incidents critiques = moins d'astreintes surprises, plus de soirées tranquilles pour les équipes. La fiabilité fait partie du bien-être au travail."</span></p>
      </ModalRegister>

      {/* Post-it */}
      <ModalRegister open={showPostItModal} title="Notes griffonnées" onClose={() => setShowPostItModal(false)} onHint={() => setShowHintModal(true)}>
        <p className="text-sm">Des notes difficiles à lire posées sur le bureau :</p>
        <p className="mt-2 italic text-sm text-gray-200">"Penser à ranger les schémas DANS LE TIROIR cette fois.<br />Et arrêter de tout rebrancher au hasard, MERCI."<br /><span className="text-xs text-right block">Jean-Pierre</span></p>
        <p className="mt-2 text-[11px] text-gray-300">Quelqu'un a ajouté au stylo : <span className="italic">"Et penser à faire tourner les astreintes : tout le monde a droit à des soirées sans coup de fil du data center."</span></p>
        <p className="mt-2 text-[11px] text-gray-400 italic text-center">En bas de la feuille, on voit un schéma : «</p>
        <div className="rounded-xl border border-white/10 bg-black/40 p-3 flex justify-center">
          <img src="/assets/p5_note_schema.webp" alt="Schéma griffonné avec rectangles, cercles et triangles" className="w-full max-w-sm h-auto object-contain" />
        </div>
        <p className="mt-2 text-[11px] text-gray-400 text-center">Au dos, une petite mention : <span className="italic">"Merci d'éteindre la lumière de la salle technique en partant — même les serveurs ont droit à une ambiance responsable."</span></p>
      </ModalRegister>

      {/* Charte */}
      <Modal open={showCharterModal} title="Charte numérique responsable (extrait)" onClose={() => setShowCharterModal(false)}>
        <p className="text-sm text-gray-200 mb-2">Une affiche plastifiée est rangée dans le classeur. En bas, on lit : <span className="font-mono text-xs bg-black/60 px-2 py-1 rounded ml-1">"Engagement interne — numérique responsable & bien-être au travail"</span></p>
        <ul className="mt-2 text-xs space-y-2 list-disc pl-4">
          <li><span className="font-semibold">Énergie & climat :</span> optimiser l'usage des serveurs, éviter les sur-capacités inutiles et privilégier les fenêtres de maintenance raisonnables (ODD 7 & 13).</li>
          <li><span className="font-semibold">Matériel :</span> prolonger la durée de vie des équipements, recycler et reconditionner ce qui peut l'être (ODD 12).</li>
          <li><span className="font-semibold">Humain :</span> limiter les interventions nocturnes, organiser la rotation des astreintes, rester attentifs aux signaux de fatigue des équipes (ODD 3 & 8).</li>
          <li><span className="font-semibold">Inclusion :</span> concevoir des outils qui fonctionnent pour tout le monde, en tenant compte de l'accessibilité et des différents profils utilisateur.</li>
          <li><span className="font-semibold">IA & communication :</span> Origami aide à repérer les signaux faibles, mais ne remplace jamais les discussions directes ni le respect dans les échanges.</li>
        </ul>
      </Modal>

      {/* Reboot */}
      <OrigamiCommunicationModal open={showOrigamiReboot} text={rebootText} onClose={() => setShowOrigamiReboot(false)} autoClose={true} autoCloseDuration={10000} />

      {/* Indices */}
      <HintsModalHub open={showHintModal} onClose={() => setShowHintModal(false)} title="Indices" categories={hintCategories} markHintUsedOnce={markHintUsedOnce} usedStorageKey={usedStorageKey} />
    </div>
  );
}